import type { Id, Version } from './common';
import type { UserStatus } from './identity';

/**
 * The permission catalogue (spec §2) and the shapes of roles and their assignments. The catalogue is the one list:
 * migration 013 writes the same entries into its CHECK (a test compares the two), the server checks against it and
 * the web builds its matrix from it. Adding a permission here is the only way to add one.
 */

/** The kinds of object an assignment can be limited to. */
export const SCOPE_OBJECT_TYPES = ['objective', 'project'] as const;
export type ScopeObjectType = typeof SCOPE_OBJECT_TYPES[number];

export type PermissionAction = 'view' | 'edit' | 'delete' | 'configure' | 'moderate';
export type PermissionModule = 'projects' | 'finance' | 'strategy' | 'partners' | 'workplace' | 'audit' | 'users' | 'roles' | 'ai';

/**
 * Every permission, in the order the matrix shows them. `objectType` says what an `objects` assignment limits the
 * permission to; `null` means the permission always holds company-wide (spec §3). `edit`, `delete` and `configure`
 * imply `view` of the same module on the same scope; `moderate` implies nothing.
 */
export const PERMISSIONS = {
  'projects.view': { module: 'projects', action: 'view', objectType: 'project' },
  'projects.edit': { module: 'projects', action: 'edit', objectType: 'project' },
  'projects.delete': { module: 'projects', action: 'delete', objectType: 'project' },
  'finance.view': { module: 'finance', action: 'view', objectType: null },
  'finance.edit': { module: 'finance', action: 'edit', objectType: null },
  'finance.delete': { module: 'finance', action: 'delete', objectType: null },
  'finance.configure': { module: 'finance', action: 'configure', objectType: null },
  'strategy.view': { module: 'strategy', action: 'view', objectType: 'objective' },
  'strategy.edit': { module: 'strategy', action: 'edit', objectType: 'objective' },
  'strategy.delete': { module: 'strategy', action: 'delete', objectType: 'objective' },
  'partners.view': { module: 'partners', action: 'view', objectType: null },
  'partners.edit': { module: 'partners', action: 'edit', objectType: null },
  'partners.delete': { module: 'partners', action: 'delete', objectType: null },
  'workplace.moderate': { module: 'workplace', action: 'moderate', objectType: null },
  'audit.view': { module: 'audit', action: 'view', objectType: null },
  'users.view': { module: 'users', action: 'view', objectType: null },
  'users.edit': { module: 'users', action: 'edit', objectType: null },
  'users.delete': { module: 'users', action: 'delete', objectType: null },
  'roles.view': { module: 'roles', action: 'view', objectType: null },
  'roles.edit': { module: 'roles', action: 'edit', objectType: null },
  'roles.delete': { module: 'roles', action: 'delete', objectType: null },
  'ai.configure': { module: 'ai', action: 'configure', objectType: null },
} as const satisfies Record<string, { module: PermissionModule; action: PermissionAction; objectType: ScopeObjectType | null }>;

export type Permission = keyof typeof PERMISSIONS;
export const PERMISSION_LIST = Object.keys(PERMISSIONS) as Permission[];

/** One object an assignment is limited to. */
export type ObjectRef = { type: ScopeObjectType; id: Id };
/** What a principal holds of one permission: company-wide, or only these objects. */
export type PermissionGrant = 'all' | ReadonlyArray<ObjectRef>;
/** A principal's permissions; a permission it does not hold is absent. */
export type PermissionMap = Partial<Record<Permission, PermissionGrant>>;

/** What every Fastify route declares in its `config` (checked at boot). */
export type RouteAuthz = { permission: Permission } | { authz: 'public' | 'self' | 'home' };

/** The stable key of a default role; bootstrap, recovery, the invitation default and tests find roles by it. */
export type RoleSeedKey = 'system' | 'bod' | 'strategy_lead' | 'finance_lead';

/** The TS copy of the permission sets migration 013 seeds (the system role holds the whole catalogue). */
export const DEFAULT_ROLE_PERMISSIONS: Record<Exclude<RoleSeedKey, 'system'>, readonly Permission[]> = {
  bod: ['projects.view', 'projects.edit', 'projects.delete', 'finance.view', 'strategy.view', 'partners.view',
    'partners.edit', 'audit.view'],
  strategy_lead: ['strategy.view', 'strategy.edit', 'strategy.delete'],
  finance_lead: ['finance.view', 'finance.edit', 'finance.delete'],
};

/**
 * A role as the roles page reads it. The system role's `permissions` is `PERMISSION_LIST`, derived: it has no
 * `role_permissions` rows (spec §3).
 */
export type RoleDTO = { id: Id; name: string; description: string | null; isSystem: boolean; seedKey: RoleSeedKey | null;
  permissions: Permission[]; assignedCount: number; version: Version };
export type RoleBody = { name: string; description?: string | null; permissions: Permission[] };
export type RolePatchBody = RoleBody & { version: Version };

export type AssignmentScopeInput = 'all' | ObjectRef[];
/** An object of a scope, named. Objective: `label` = title, `hint` = `Năm ${year}`; project: `label` = `${code} · ${name}`, `hint` = its Space. */
export type ScopeObjectDTO = { type: ScopeObjectType; id: Id; label: string; hint: string | null; deleted: boolean };
export type AssignableUserDTO = { id: Id; name: string; email: string; status: 'active' | 'pending'; roleIds: Id[] };
export type RoleAssignmentDTO = { id: Id; user: { id: Id; name: string; status: UserStatus }; role: { id: Id; name: string };
  scope: 'all' | ScopeObjectDTO[]; assignedBy: { id: Id; name: string } | null; assignedAt: string; version: Version };
export type AssignmentBody = { userId: Id; roleId: Id; scope: AssignmentScopeInput };
export type AssignmentPatchBody = { scope: AssignmentScopeInput; version: Version };
/** One role an account holds, as a chip: its scope is company-wide or a count of objects per type. */
export type UserRoleChip = { assignmentId: Id; roleId: Id; name: string; isSystem: boolean;
  scope: 'all' | ReadonlyArray<{ type: ScopeObjectType; count: number }> };
